import { Delete, X } from 'lucide-react'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { VolumeIcon, type VolumeIconHandle } from '@/components/ui/animated-icons/volume'
import { CashRegisterSettings } from '@invo.space/shared'
import { cn } from '@/lib/utils'

import { useKeySound } from './useKeySound'

interface KeyProps {
  children: ReactNode
  className?: string
  active?: boolean
  primary?: boolean
  disabled?: boolean
  label?: string
  onClick: () => void
  onPressDown?: () => void
  onPressUp?: () => void
}

/** Klávesa bez rámečku, jemná plocha, výška 64 px kvůli dotyku. */
const Key = ({ children, className, active, primary, disabled, label, onClick, onPressDown, onPressUp }: KeyProps) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={() => !disabled && onClick()}
    onPointerDown={() => !disabled && onPressDown?.()}
    onPointerUp={() => !disabled && onPressUp?.()}
    className={cn(
      'inline-flex h-16 items-center justify-center rounded-xl text-2xl font-medium tabular transition-[background-color,scale] duration-150 ease-out select-none',
      'active:scale-[0.97] focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none disabled:opacity-40 disabled:active:scale-100',
      primary
        ? 'bg-primary text-base text-primary-foreground hover:bg-primary/90'
        : active
          ? 'bg-foreground text-background'
          : 'bg-muted hover:bg-neutral-bg',
      className,
    )}
  >
    {children}
  </button>
)

export interface NumpadProps {
  cashierSettings: CashRegisterSettings
  isVatPayer?: boolean
  /** Klávesnice je viditelná, poslouchá fyzickou klávesnici */
  active?: boolean
  /** Stejná signatura jako dřív: první číslo (před ×) nebo 1, druhé číslo, sazba DPH */
  onPlus: (amount: number, price: number, tax?: number) => void
}

const displayNumber = (value: string) => (value === '' ? '0' : value.replace('.', ','))

/** Rychlá klávesnice pro vlastní položku: částka, volitelně násobení a sazba DPH. */
export const Numpad = ({ cashierSettings, isVatPayer, active = true, onPlus }: NumpadProps) => {
  const { sound, enabled: soundOn, toggle: toggleSound } = useKeySound()
  const volumeRef = useRef<VolumeIconHandle>(null)
  useEffect(() => {
    if (soundOn) volumeRef.current?.startAnimation()
    else volumeRef.current?.stopAnimation()
  }, [soundOn])
  const [buffer, setBuffer] = useState('')
  const [buffer2, setBuffer2] = useState('')
  const [timesActive, setTimesActive] = useState(false)
  const [tax, setTax] = useState<{ id: string; percentage: number } | undefined>(undefined)

  useEffect(() => {
    if (cashierSettings.defaultTaxRate) {
      setTax({
        id: String(cashierSettings.defaultTaxRate.id),
        percentage: Number(cashierSettings.defaultTaxRate.percentage),
      })
    }
  }, [cashierSettings.defaultTaxRate])

  const typeNumber = (number: string) => {
    const positionOfComma = buffer.indexOf('.')
    if (positionOfComma !== -1 && buffer.length - positionOfComma > 2) return
    if (number === '0' && buffer === '') return
    setBuffer(buffer + number)
  }

  const toggleTimes = () => {
    if (timesActive) {
      setBuffer(buffer2)
      setBuffer2('')
    } else {
      if (buffer === '') return
      setBuffer2(buffer)
      setBuffer('')
    }
    setTimesActive(!timesActive)
  }

  const handleBackspace = () => {
    if (buffer === '' && timesActive) return toggleTimes()
    setBuffer(buffer === '0.' ? '' : buffer.substring(0, buffer.length - 1))
  }

  const handleClear = () => {
    setBuffer('')
    setBuffer2('')
    setTimesActive(false)
  }

  const handleDecimalPoint = () => {
    if (buffer.indexOf('.') === -1) setBuffer(buffer.length === 0 ? '0.' : buffer + '.')
  }

  const handleAdd = () => {
    if (buffer === '' || buffer === '0.' || Number(buffer) === 0) return
    onPlus(buffer2 === '' ? 1 : parseFloat(buffer2), parseFloat(buffer), isVatPayer ? tax?.percentage : undefined)
    handleClear()
  }

  // fyzická klávesnice (stolní pokladna): číslice, čárka, Backspace, Esc, * a Enter
  const handlers = useRef({ typeNumber, handleBackspace, handleClear, handleDecimalPoint, handleAdd, toggleTimes })
  handlers.current = { typeNumber, handleBackspace, handleClear, handleDecimalPoint, handleAdd, toggleTimes }

  const onKeyDown = useCallback((event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
    if (event.metaKey || event.ctrlKey || event.altKey) return
    const h = handlers.current
    if (/^[0-9]$/.test(event.key)) h.typeNumber(event.key)
    else if (event.key === ',' || event.key === '.') h.handleDecimalPoint()
    else if (event.key === 'Backspace') h.handleBackspace()
    else if (event.key === 'Escape') h.handleClear()
    else if (event.key === '*' || event.key === 'x') h.toggleTimes()
    else if (event.key === 'Enter') h.handleAdd()
    else return
    event.preventDefault()
  }, [])

  useEffect(() => {
    if (!active) return
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [active, onKeyDown])

  const taxRates = cashierSettings.taxRates ?? []
  const firstLabel = cashierSettings.amountThenPrice ? 'Množství' : 'Cena'
  const idleLabel = cashierSettings.amountThenPrice ? 'Cena, nebo množství a ×' : 'Cena'
  const secondLabel = cashierSettings.amountThenPrice ? 'Cena' : 'Množství'

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-3">
      <div className="px-1 pt-2 pb-4">
        <div className="flex items-baseline justify-between text-xs text-muted-foreground">
          <span>{timesActive ? `${firstLabel} × ${secondLabel.toLowerCase()}` : idleLabel}</span>
          <span className="flex items-center gap-2">
            {!timesActive && <span>× pro více kusů</span>}
            <button
              type="button"
              onClick={toggleSound}
              aria-pressed={soundOn}
              aria-label={soundOn ? 'Vypnout zvuk kláves' : 'Zapnout zvuk kláves'}
              title={soundOn ? 'Vypnout zvuk kláves' : 'Zapnout zvuk kláves'}
              className="-my-1 -mr-2 grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              <VolumeIcon ref={volumeRef} size={16} defaultActive={soundOn} aria-hidden />
            </button>
          </span>
        </div>
        <div className="mt-2 flex items-baseline justify-end gap-2 tabular" aria-live="polite">
          {timesActive && <span className="text-2xl text-muted-foreground">{displayNumber(buffer2)} ×</span>}
          <span className={cn('text-5xl leading-none font-medium tracking-[-0.03em]', buffer === '' && 'text-muted-foreground')}>
            {displayNumber(buffer)}
          </span>
          {!(timesActive && !cashierSettings.amountThenPrice) && <span className="text-xl text-muted-foreground">Kč</span>}
        </div>
      </div>

      {isVatPayer && taxRates.length > 0 && (
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(taxRates.length, 4)}, minmax(0, 1fr))` }} role="radiogroup" aria-label="Sazba DPH">
          {taxRates.map((rate) => {
            const selected = tax?.id === String(rate.id)
            return (
              <button
                key={rate.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setTax({ id: String(rate.id), percentage: Number(rate.percentage) })}
                className={cn(
                  'inline-flex h-11 items-center justify-center rounded-full text-sm font-medium tabular transition-colors select-none',
                  'focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                  selected ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                DPH {rate.percentage} %
              </button>
            )
          })}
        </div>
      )}

      <div className="grid grid-cols-4 gap-2">
        <Key onClick={() => typeNumber('7')} {...sound}>7</Key>
        <Key onClick={() => typeNumber('8')} {...sound}>8</Key>
        <Key onClick={() => typeNumber('9')} {...sound}>9</Key>
        <Key onClick={handleBackspace} disabled={buffer === '' && buffer2 === ''} label="Smazat číslici" className="text-muted-foreground" {...sound}>
          <Delete className="size-6" aria-hidden />
        </Key>

        <Key onClick={() => typeNumber('4')} {...sound}>4</Key>
        <Key onClick={() => typeNumber('5')} {...sound}>5</Key>
        <Key onClick={() => typeNumber('6')} {...sound}>6</Key>
        <Key onClick={handleClear} disabled={buffer === '' && buffer2 === ''} label="Vymazat" className="text-lg text-muted-foreground" {...sound}>
          C
        </Key>

        <Key onClick={() => typeNumber('1')} {...sound}>1</Key>
        <Key onClick={() => typeNumber('2')} {...sound}>2</Key>
        <Key onClick={() => typeNumber('3')} {...sound}>3</Key>
        <Key onClick={toggleTimes} active={timesActive} disabled={buffer === '' && !timesActive} label="Násobit" {...sound}>
          <X className="size-6" aria-hidden />
        </Key>

        <Key onClick={() => typeNumber('0')} className="col-span-2" {...sound}>0</Key>
        <Key onClick={handleDecimalPoint} label="Desetinná čárka" {...sound}>,</Key>
        <Key onClick={handleAdd} primary disabled={buffer === '' || Number(buffer) === 0} {...sound}>
          Přidat
        </Key>
      </div>
    </div>
  )
}
